import { defineStore } from "pinia";
import {
  StorageSerializers,
  useStorage,
  type RemovableRef,
} from "@vueuse/core";

import type {
  ProductDetail,
  RequestDetail,
  Insured,
  Policy,
  PolicyCard,
} from "@/interfaces/product.interface";
import {
  fetchPolicies,
  fetchProductByID,
  fetchRequestDetail,
  fetchRequests,
} from "@/services/product.services";
import { getRamoName } from "../utils/get-ramo-name";

interface ProductStore {
  policies: RemovableRef<Policy[]>;
  products: RemovableRef<PolicyCard[]>;
  currentPolicy: Policy;
  currentProduct: RemovableRef<PolicyCard | null>;
}

const handleError = (error: unknown) => {
  throw error;
};

export const useProductStore = defineStore("product", {
  state: (): ProductStore => ({
    policies: useStorage("policies", []),
    products: useStorage("products", []),
    currentPolicy: {} as Policy,
    currentProduct: useStorage<PolicyCard | null>(
      "currentProduct",
      null,
      undefined,
      {
        serializer: StorageSerializers.object,
      }
    ),
  }),

  getters: {
    insuredInfo(state): Insured {
      return state.currentPolicy.asegurado ?? {};
    },
    groupedPolicies(state) {
      const grouped = state.products.reduce((groups: any, policy: any) => {
        const key = policy.product_cod_ramo_bco;

        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(policy);
        return groups;
      }, {});

      // Ordenar las keys alfabéticamente y reconstruir el objeto
      const sortedGroups = Object.keys(grouped)
        .sort()
        .reduce((sorted: any, key: string) => {
          sorted[key] = grouped[key];
          return sorted;
        }, {});

      return sortedGroups;
    },
    groupedPoliciesWithOther() {
      const grouped: Record<string, any> = { ...this.groupedPolicies };
      const otherPolicies = [];

      for (const codRamo in grouped) {
        if (getRamoName(codRamo) === "Otro") {
          otherPolicies.push(...grouped[codRamo]);
          delete grouped[codRamo];
        }
      }

      if (otherPolicies.length) {
        grouped["otro"] = otherPolicies;
      }

      return grouped;
    },
  },

  actions: {
    setPolicies(policies: Policy[]): void {
      this.policies = policies;
    },

    setCurrentPolicy(policy: Policy | any): void {
      this.currentPolicy = policy;
    },

    setCurrentProduct(product: PolicyCard | any): void {
      this.currentProduct = product;
    },

    async loadProducts(): Promise<void> {
      try {
        const { data } = await fetchPolicies();
        this.products = data;
      } catch (error) {
        handleError(error);
      }
    },

    async loadProduct(id: string): Promise<ProductDetail> {
      const { data } = await fetchProductByID(id);
      this.setPolicies(data.polizas);
      return data;
    },

    async loadRequests() {
      try {
        const response = await fetchRequests();
        const data = response.data;
        return data;
      } catch (e) {
        console.log(e);
      }
    },

    async loadRequestDetail(id: string | number): Promise<RequestDetail> {
      const { data } = await fetchRequestDetail(id);
      return data;
    },
  },
});
